import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { MedalCategory } from "../api/types";

/**
 * Round 30 point 5 — Medal categories, entirely admin-defined, same
 * pattern as Mall Categories. The Android Medals tab's category tabs
 * read this table directly, in sort_order.
 */
export function MedalCategoriesPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<MedalCategory>("/admin/medal-categories");
  const [editing, setEditing] = useState<Partial<MedalCategory> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function save() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = { name: editing.name, sort_order: Number(editing.sort_order) || 0, is_active: editing.is_active ?? true };
      if (editing.id) {
        await apiRequest(`/admin/medal-categories/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/medal-categories", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save category", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: MedalCategory) {
    try {
      await apiRequest(`/admin/medal-categories/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update category", "error");
    }
  }

  async function remove(row: MedalCategory) {
    try {
      await apiRequest(`/admin/medal-categories/${row.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete category", "error");
    }
  }

  const columns: Column<MedalCategory>[] = [
    { header: "Name", render: (row) => <p className="font-medium text-ink">{row.name}</p> },
    { header: "Sort Order", render: (row) => row.sort_order },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>Edit</Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
          <Button size="sm" variant="danger" onClick={() => remove(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Medal Categories">
      <p className="mb-4 text-xs text-ink-faint">
        Medals (the other Medal page) pick one of these categories — deleting a category here doesn't delete any
        medal or grant that referenced it, it just leaves that medal uncategorized.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ is_active: true, sort_order: 0 })}>+ Add Category</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No medal categories yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Category" : "Add Category"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} isLoading={isSubmitting}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input
            label="Sort order"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          />
        </div>
      </Modal>
    </AppShell>
  );
}
